import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import QRCode from 'react-qr-code';

import { RegisterStage, StatusCode } from '../../../../api/enumerations';
import { totpEnable } from '../../../../api/register';
import { ButtonBox } from '../../../../components/UI/Box';
import {
  IconContentCopyMS,
  IconQrCodeScannerMS,
} from '../../../../constants/icons';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../hooks/useSnackbar';
import OTPInput from '../OTPInput';
import {
  FilledButton,
  GoogleAuthContainer,
  HowToScanner,
  ListItem,
  OutlinedStyledButton,
  QRCodeContainer,
  SecretBox,
  SectionDialog,
  TitleItem,
} from '../styles';

interface GoogleAuthStepProps {
  data: {
    secret: string;
    otp_auth_url: string;
  };
  setNext: Dispatch<SetStateAction<boolean>>;
  handleNextStage: (stage: RegisterStage) => void;
  token: string;
}

interface CopyToCipboardProps {
  text: string;
}

function CopyToCipboard({ text }: CopyToCipboardProps): JSX.Element {
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      handleSnackbar('copiado!', false);
    } catch (err) {
      handleSnackbar(getErrorMessage(err), true);
    }
  };
  return (
    <IconButton color="primary" onClick={handleCopy}>
      {IconContentCopyMS}
    </IconButton>
  );
}

export function GoogleAuthStep({
  setNext,
  data,
  handleNextStage,
  token,
}: GoogleAuthStepProps): JSX.Element {
  const [pageCodeIsVisible, setPageCodeIsVisible] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const handleEnableTOTPCode = useCallback(async () => {
    setLoading(true);

    try {
      const { detail } = await totpEnable({ code, token });

      if (detail.description) throw new Error(detail.description);

      if (detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      localStorage.setItem(
        'security-auth',
        JSON.stringify({ isEnable: true, authFactor: '2' })
      );
      handleNextStage(RegisterStage.CONFIRMATION_AUTH_METHOD);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSnackbar]);

  return (
    <GoogleAuthContainer>
      {!pageCodeIsVisible ? (
        <>
          <SectionDialog>
            Escaneie o código QR com seu aplicativo (ex: Google Authenticator)
          </SectionDialog>
          <QRCodeContainer>
            <div>
              <QRCode value={data.otp_auth_url} />
            </div>
          </QRCodeContainer>
          <HowToScanner>
            <TitleItem>
              {IconQrCodeScannerMS} <strong>Como escanear</strong>
            </TitleItem>
            <ListItem>
              1. Abra o seu aplicativo (ex: Google Authenticator)
            </ListItem>
            <ListItem>
              2. Toque em &quot;+&quot; ou &quot;Adicionar conta&quot;
            </ListItem>
            <ListItem>3. Selecione &quot;Escanear o código QR&quot;</ListItem>
            <ListItem>4. Aponte a câmera para o QR code acima</ListItem>
          </HowToScanner>
          <Typography fontSize="20px" fontWeight={600} m="16px 0 8px">
            Ou insira manualmente a chave secreta:
          </Typography>
          <SecretBox>
            <span>{data.secret}</span>
            <CopyToCipboard text={data.secret} />
          </SecretBox>
          <ButtonBox sx={{ gap: '8px', marginTop: '32px' }}>
            <FilledButton onClick={() => setPageCodeIsVisible(true)}>
              continuar
            </FilledButton>
            <OutlinedStyledButton onClick={() => setNext(false)}>
              voltar
            </OutlinedStyledButton>
          </ButtonBox>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SectionDialog>
              <strong>Digite o código de verificação</strong>
            </SectionDialog>
            <SectionDialog sx={{ margin: '48px 0', fontSize: '15px' }}>
              Insira o código de 6 dígitos do seu aplicativo (ex: Google
              Authenticator)
            </SectionDialog>
            <OTPInput length={6} value={code} onChange={setCode} />
            <Typography sx={{ margin: '32px 0' }}>
              Insira o código de 6 dígitos
            </Typography>
          </Box>
          <ButtonBox sx={{ gap: '8px', margin: '32px 0' }}>
            <FilledButton onClick={handleEnableTOTPCode}>
              {loading ? (
                <CircularProgress sx={{ color: 'white' }} size={24} />
              ) : (
                'verificar'
              )}
            </FilledButton>
            <OutlinedStyledButton onClick={() => setPageCodeIsVisible(false)}>
              voltar
            </OutlinedStyledButton>
          </ButtonBox>
        </>
      )}
    </GoogleAuthContainer>
  );
}
